import React, { useMemo } from 'react';
import { Icon, Tooltip } from '@fashionnovateam/nova-ui-toolkit';
import { CheckboxCell } from '../cells';
import {
  requiredColumns,
  baselineCostColumns,
  optionalColumns,
  actionColumn,
  checkboxColumn,
  completedExtraColumns,
  requestedDateColumn,
} from '../../columns';
import {
  BASELINE_COST_TOOLTIP,
  EXCLUSIVITY_TOOLTIP,
  DELIVERY_DATE_TOOLTIP,
  STOCK_IHD_TOOLTIP,
} from '../../../../Lib/Constants/ui-text';

type UseDataGridColumnsParams = {
  isBaselineCostEnabled: boolean;
  isCompleted: boolean;
  showBulkActions?: boolean;
  isAllSelected?: boolean;
  isPartiallySelected?: boolean;
  onToggleAll?: () => void;
};

export function useDataGridColumns({
  isBaselineCostEnabled,
  isCompleted,
  showBulkActions = false,
  isAllSelected = false,
  isPartiallySelected = false,
  onToggleAll,
}: UseDataGridColumnsParams) {
  return useMemo(() => {
    let cols = [...requiredColumns];

    const exclusivityIndex = cols.findIndex(
      (col) => col.field === 'exclusivity',
    );
    if (exclusivityIndex >= 0) {
      cols[exclusivityIndex] = {
        ...cols[exclusivityIndex],
        renderHeader: () => (
          <div className="flex gap-1 items-center">
            FN Exclusive
            <Tooltip content={EXCLUSIVITY_TOOLTIP} position="top" arrow>
              <Icon
                name="question/question-mark-circle"
                className="text-neutral-400 cursor-help"
                size="16"
              />
            </Tooltip>
          </div>
        ),
      };
    }

    const deliveryDateIndex = cols.findIndex(
      (col) => col.field === 'earliest_custom_cut_delivery_date',
    );
    if (deliveryDateIndex >= 0) {
      cols[deliveryDateIndex] = {
        ...cols[deliveryDateIndex],
        renderHeader: () => (
          <div className="flex gap-1 items-center">
            Delivery Date
            <Tooltip content={DELIVERY_DATE_TOOLTIP} position="top" arrow>
              <Icon
                name="question/question-mark-circle"
                className="text-neutral-400 cursor-help"
                size="16"
              />
            </Tooltip>
          </div>
        ),
      };
    }

    const plusCostIndex =
      cols.findIndex((col) => col.field === 'cost_plus') + 1;
    if (isBaselineCostEnabled && plusCostIndex > 0) {
      const baselineColumnsWithTooltips = baselineCostColumns.map((col) => ({
        ...col,
        renderHeader: () => (
          <div className="flex gap-1 items-center">
            {col.headerName}
            <Tooltip
              content={BASELINE_COST_TOOLTIP}
              position="top"
              arrow
              zIndex={20}
            >
              <Icon
                name="question/question-mark-circle"
                className="text-neutral-400 cursor-help"
                size="16"
              />
            </Tooltip>
          </div>
        ),
      }));
      cols.splice(plusCostIndex, 0, ...baselineColumnsWithTooltips);
    }

    cols = [...cols, ...optionalColumns];

    const stockIHDIndex = cols.findIndex(
      (col) => col.field === 'earliest_IHD_date_to_deliver_stock',
    );
    if (stockIHDIndex >= 0) {
      cols[stockIHDIndex] = {
        ...cols[stockIHDIndex],
        renderHeader: () => (
          <div className="flex gap-1 items-center">
            Stock IHD
            <Tooltip content={STOCK_IHD_TOOLTIP} position="top" arrow>
              <Icon
                name="question/question-mark-circle"
                className="text-neutral-400 cursor-help"
                size="16"
              />
            </Tooltip>
          </div>
        ),
      };
    }

    const productIndex = cols.findIndex((col) => col.field === 'product') + 1;
    if (isCompleted) {
      cols.splice(productIndex, 0, ...completedExtraColumns);
      cols = cols.filter((col) => col.field !== 'submit');
    } else {
      cols.splice(productIndex, 0, requestedDateColumn);
      cols.push(actionColumn);
    }

    if (showBulkActions && !isCompleted) {
      const checkboxCol = {
        ...checkboxColumn,
        renderHeader: () =>
          onToggleAll ? (
            <CheckboxCell
              checked={isAllSelected}
              indeterminate={isPartiallySelected}
              onChange={onToggleAll}
            />
          ) : null,
      };
      cols = [checkboxCol, ...cols];
    }

    return cols;
  }, [
    isBaselineCostEnabled,
    isCompleted,
    showBulkActions,
    isAllSelected,
    isPartiallySelected,
    onToggleAll,
  ]);
}
